import OrganizationTaskCategoryFields from "./OrganizationTaskCategoryFields";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import axios from "axios";
import Select from "react-select";
const TaskAssignmentFields = forwardRef(({
  taskId,
  disabled
}, ref) => {
  const [options, setOptions] = useState(null),
    [programIds, setProgramIds] = useState([]),
    [orgIds, setOrgIds] = useState([]);
  const [syncCurrent, setSyncCurrent] = useState(false),
    [error, setError] = useState("");
  const [syncProgress, setSyncProgress] = useState(null);
  const [orgCategoryRules, setOrgCategoryRules] = useState({});
  const base = process.env.REACT_APP_API_BASE_URL,
    token = localStorage.getItem("token");
  useEffect(() => {
    let active = true;
    setOptions(null);
    setError("");
    setSyncCurrent(false);
    axios.get(`${base}/api/task-assignments/options`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(({
      data
    }) => {
      if (!active) return;
      setOptions(data);
      setProgramIds(data.programs.filter(p => p.taskIds.includes(Number(taskId))).map(p => p.id));
      setOrgIds(data.orgs.filter(o => o.taskIds.includes(Number(taskId))).map(o => o.id));
      setOrgCategoryRules(Object.fromEntries(data.orgs.filter(o => o.taskIds.includes(Number(taskId))).map(org => [org.id, org.categoryRules?.[taskId] ?? null])));
    }).catch(() => {
      if (active) setError("Could not load task assignments. Reload before saving.");
    });
    return () => {
      active = false;
    };
  }, [base, token, taskId]);
  const validateCategoryChoices = () => {
    if (orgIds.some(id => Array.isArray(orgCategoryRules[id]) && !orgCategoryRules[id].length)) {
      throw new Error("Select employee categories for each limited organization before saving.");
    }
  };
  useImperativeHandle(ref, () => ({
    assertReady() {
      if (!options) throw new Error(error || "Task assignments are still loading. Please try again.");
      validateCategoryChoices();
    },
    async save(id) {
      if (!options) throw new Error("Task assignments are not loaded.");
      validateCategoryChoices();
      const headers = {
        Authorization: `Bearer ${token}`
      };
      const {
        data
      } = await axios.put(`${base}/api/task-assignments/tasks/${id}`, {
        programIds,
        orgIds,
        orgCategoryRules: Object.fromEntries(orgIds.map(id => [id, orgCategoryRules[id] ?? null])),
        syncCurrent
      }, {
        headers
      });
      if (data.job) {
        let job = data.job;
        setSyncProgress(job.progress);
        while (job.status === "running") {
          await new Promise(resolve => setTimeout(resolve, 1500));
          const response = await axios.get(`${base}/api/task-assignments/sync/${job.id}`, {
            headers
          });
          job = response.data.job;
          setSyncProgress(job.progress);
        }
        setSyncProgress(null);
        if (job.status !== "complete") throw new Error(job.message || "Assignments saved, but current-user sync failed.");
      }
    }
  }));
  const select = (label, rows, values, setValues) => <div className="col-md-6 mb-3"><label className="form-label" htmlFor={`task-assignment-${label}`}>{label}</label>
    <Select inputId={`task-assignment-${label}`} isMulti isDisabled={disabled || !options} options={(rows || []).map(r => ({
      value: r.id,
      label: r.name
    }))} value={(rows || []).filter(r => values.includes(r.id)).map(r => ({
      value: r.id,
      label: r.name
    }))} onChange={v => setValues(v.map(o => o.value))} classNamePrefix="react-select" /></div>;
  return <section className="border rounded p-3 mb-3"><h5>Program and Organization Assignments</h5>
    <p className="text-muted small">Save this task in the selected programs and organizations for future users. Organization tasks follow the organization on the user's position.</p>
    {syncProgress !== null && <p role="status">Syncing this task to current users: {syncProgress}%</p>}
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {!options && !error && <p role="status">Loading assignments...</p>}
    <div className="row">{select("Programs", options?.programs, programIds, setProgramIds)}{select("Organizations", options?.orgs, orgIds, setOrgIds)}</div>
    {(options?.orgs || []).filter(org => orgIds.includes(org.id)).map(org => (
      <div key={org.id} className="border rounded p-3 mb-3">
        <h6>{org.name}</h6>
        <OrganizationTaskCategoryFields label={org.name} categories={options.categories || []}
          categoryIds={orgCategoryRules[org.id] ?? null} disabled={disabled}
          onChange={value => setOrgCategoryRules(previous => ({...previous, [org.id]: value}))} />
      </div>
    ))}
    {options?.canSync && <label className="form-check"><input className="form-check-input" type="checkbox" checked={syncCurrent} disabled={disabled} onChange={e => setSyncCurrent(e.target.checked)} /><span className="form-check-label">Also sync this task to current associates and candidates when saving</span></label>}
    <div className="text-muted small mt-2">Existing task history is preserved. A task shared by a program and organization is assigned once.</div>
  </section>;
});
export default TaskAssignmentFields;
